import { useState, useRef, useEffect, useCallback } from 'react';
import { getTodayKey } from './complianceUtils';

function formatDateLabel(dateStr) {
    const d = new Date(dateStr + 'T00:00:00');
    const day = d.toLocaleDateString('en-US', { weekday: 'short' });
    const month = d.getMonth() + 1;
    const date = d.getDate();
    return `${day}, ${month}/${date}`;
}

function DayPicker({ history, selectedDate, onSelectDate }) {
    const todayKey = getTodayKey();
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    const longPressRef = useRef(null);

    // Find current day's status from history
    const selectedDay = history.find(d => d.date === selectedDate);
    const selectedStatus = selectedDay?.status || 'none';
    const isToday = selectedDate === todayKey;

    const handleOpen = useCallback(() => setOpen(true), []);

    const handleMouseDown = useCallback(() => {
        longPressRef.current = setTimeout(handleOpen, 400);
    }, [handleOpen]);

    const handleMouseUp = useCallback(() => {
        clearTimeout(longPressRef.current);
    }, []);

    const handleClick = useCallback(() => {
        clearTimeout(longPressRef.current);
        setOpen(prev => !prev);
    }, []);

    const handleSelect = useCallback((date) => {
        onSelectDate(date);
        setOpen(false);
    }, [onSelectDate]);

    // Close dropdown on outside click
    useEffect(() => {
        if (!open) return;
        const handleOutside = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('pointerdown', handleOutside);
        return () => document.removeEventListener('pointerdown', handleOutside);
    }, [open]);

    return (
        <div className="cb-datepick" ref={rootRef}>
            <button
                type="button"
                className="cb-datepick-btn"
                onClick={handleClick}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onTouchStart={handleMouseDown}
                onTouchEnd={handleMouseUp}
            >
                <span className={`cb-dot cb-dot--${selectedStatus} ${isToday ? 'cb-dot--today' : ''}`} />
                <span className="cb-datepick-label">{formatDateLabel(selectedDate)}</span>
                <svg className="cb-datepick-caret" width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
                    <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>

            {open && (
                <div className="cb-datepick-dropdown">
                    {history.map(day => (
                        <button
                            key={day.date}
                            type="button"
                            className={`cb-datepick-option ${day.date === selectedDate ? 'cb-datepick-option--selected' : ''}`}
                            onClick={() => handleSelect(day.date)}
                        >
                            <span className={`cb-dot cb-dot--${day.status}`} />
                            <span>{formatDateLabel(day.date)}</span>
                            {day.isToday && <span className="cb-datepick-today-tag">Today</span>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export default DayPicker;
